import React from 'react';
import InfoIcon from '@material-ui/icons/Info';
import CachedIcon from '@material-ui/icons/Cached';
import ShareIcon from '@material-ui/icons/Share';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import EditIcon from '@material-ui/icons/Edit';
import DoneIcon from '@material-ui/icons/Done';
import { SaspButton } from "./SaspButton";
import {
    Chip,
    FormControl,
    Grid,
    Input,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    Divider,
    Modal
} from "@material-ui/core";
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useAutocomplete from '@material-ui/lab/useAutocomplete';
import NoSsr from '@material-ui/core/NoSsr';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import styled from 'styled-components';
import "../styles/masterStyles.css";

const Label = styled('label')`
  padding: 0 0 4px;
  line-height: 1.5;
  display: block;
`;

const InputWrapper = styled('div')`
  width: 100%;
  border: 1px solid #d9d9d9;
  background-color: #fff;
  border-radius: 4px;
  padding: 1px;
  display: flex;
  flex-wrap: wrap;

  &:hover {
    border-color: #40a9ff;
  }

  &.focused {
    border-color: #00567a;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }

  & input {
    font-size: 14px;
    height: 30px;
    box-sizing: border-box;
    padding: 4px 6px;
    width: 0;
    min-width: 30px;
    flex-grow: 1;
    border: 0;
    margin: 0;
    outline: 0;
  }
`;

const Tag = styled(({ label, onDelete, ...props }) => (
  <div {...props}>
    <span>{label}</span>
    <CloseIcon onClick={onDelete} />
  </div>
))`
  display: flex;
  align-items: center;
  height: 24px;
  margin: 2px;
  line-height: 22px;
  background-color: #00567a;
  color: #f1f5f9;
  border: 1px solid #00567a;
  border-radius: 4px;
  box-sizing: content-box;
  padding: 0 4px 0 10px;
  outline: 0;
  overflow: hidden;

  &:focus {
    border-color: #40a9ff;
    background-color: #e6f7ff;
  }

  & span {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  & svg {
    font-size: 12px;
    cursor: pointer;
    padding: 4px;
  }
`;

const Listbox = styled('ul')`
  width: calc(100% - 32px);
  margin: 2px 0 0;
  padding: 0;
  position: absolute;
  list-style: none;
  background-color: #fff;
  overflow: auto;
  max-height: 50vh;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1;

  & li {
    padding: 5px 12px;
    display: flex;

    & span {
      flex-grow: 1;
    }

    & svg {
      color: transparent;
    }
  }

  & li[aria-selected='true'] {
    background-color: #fafafa;
    font-weight: 600;

    & svg {
      color: #1890ff;
    }
  }

  & li[data-focus='true'] {
    background-color: #e6f7ff;
    cursor: pointer;

    & svg {
      color: #000;
    }
  }
`;

const useStyles = makeStyles((theme) => ({
    paper: {
        position: 'absolute',
        backgroundColor: theme.palette.background.paper,
        border: `2px solid #00567a`,
        borderRadius: "8px",
        boxShadow: theme.shadows[5],
        padding: "8px 16px 16px",
        maxHeight: '90vh',
        minHeight: "45vh",
        width: '90vw',
        maxWidth: "300px",
        overflow: 'auto',
        outline: "none"
    },
    formControl: {
        margin: theme.spacing(1),
        width: "100%"
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    chip: {
        margin: 2,
        backgroundColor: '#00567a',
        color: '#fff' 
    },
    noLabel: {
        marginTop: theme.spacing(3),
    },
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: "100%",
        },
    },
};

const setShareList = (arr) => {
    let r = [];
    for (const item of arr) {
        r.push({title: item.displayName, value: item.mcid});
    }
    return r;
} 

const ModalForm = props => {
    let cClasses;
    if (typeof props.cClasses == 'undefined') {
        cClasses = 'margin-right-32';
    } else {
        cClasses = props.cClasses;
    }
    const shareList = setShareList(props.accessList.shareList);
    const {
        getRootProps,
        getInputLabelProps,
        getInputProps,
        getTagProps,
        getListboxProps,
        getOptionProps,
        groupedOptions,
        value,
        focused,
        setAnchorEl,
    } = useAutocomplete({
        id: 'shareWith-AC',
        defaultValue: [],
        multiple: true,
        options: shareList,
        getOptionLabel: (option) => option.title,
    });

    const classes = useStyles();
    const [open, setOpen] = React.useState(false);
    const [sendTo, setSend] = React.useState('');
    const [note, setNote] = React.useState('');
    const [subject, setSubject] = React.useState('');
    const [emailError, setEmail] = React.useState(false);
    const [noteError, setNoteError] = React.useState(false);
    const [shareWith, setShareWith] = React.useState([]);

    const handleOpen = () => {
        setOpen(true);
    };

    const handleShareWith = (event) => {
        let val = event.target.value;
        setShareWith(val);
    }

    const handleEmail = () => {
        let bool = true;
        let t = sendTo.toString();

        if (t.length < 5) {
            setEmail(false);
            return false;
        }

        t = t.replace(/\s/g, ',')
        t = t.replace(/;/g, ',')
        t = t.replace(/,,/g, ',')
        let em = t.split(',');

        for (let i of em) {
            if (/^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(i)) { bool = false; }
            setEmail(bool);
        }

    }

    const handleClose = () => {
        setSubject('');
        setSend('');
        setEmail(false);
        setOpen(false);
    };

    const handleChange = type => event => {
        switch(type) {
            case 'sendTo': {
                setSend(event.target.value);
                break;
            }
            case 'subject': {
                setSubject(event.target.value);
                break;
            }
            case 'email': {
                setSend(event.target.value);
                // handleEmail();
                break;
            }
            case 'note': {
                setNote(event.target.value);
                break;
            }
        }
    }

    const handleSubmit = (data) => event => {
        if (data.type == 'email' && emailError) {
            return false;
        }

        let obj = {
            key: props.myKey,
            mcid: props.mcid,
            type: data.type,
            shareWith: ''
        }

        if (props.deckID) { obj.deckID = props.deckID; }
        else { obj.yearvar = props.yearvar; }

        switch (obj.type) {
            case 'email': {
                if (sendTo.length <= 5) {
                    setEmail(true);
                    return false;
                }
    
                if (sendTo != '') {
                    obj.shareWith = sendTo;
                }
    
                if (subject != '') {
                    obj.message = subject;
                }
                break;
            }
            case 'share': {
                let cnt = 0;
                for (let item of value) {
                    if (cnt) { obj.shareWith += ','}
                    obj.shareWith += item.value;
                    cnt++;
                }
                break;
            }
            case 'note': {
                obj.note = note;
                obj.noteID = props.noteID;
                obj.state = 2;
                break;
            }
            case 'removeNote': {
                obj.noteID = props.noteID;
                obj.state = 0;
                break;
            }
            case 'complete': {
                obj.qyear = data.qyear;
                obj.gamemode = data.gamemode;
                break;
            }
        }
        props.func(obj);

        handleClose();
        return null;
    };

    const createMarkup = props => {
        return { __html: props };
    };

    const form = (text) => {
        let str;
        let textstr = props.textstr;

        switch(props.type) {
            case 'email': {
                str = (
                    <>
                        <Grid item xs={12}>
                            {emailError 
                                ? (
                                    <TextField
                                        error
                                        className="width100"
                                        label="To"
                                        value={sendTo}
                                        onChange={handleChange('email')}
                                        onBlur={handleEmail}
                                        multiline
                                        rowsMax={10}
                                        rows={1}
                                        helperText="Invalid Email Address"
                                    />
                                )
                                : (
                                    <TextField
                                        className="width100"
                                        label="To"
                                        value={sendTo}
                                        onChange={handleChange('email')}
                                        onBlur={handleEmail}
                                        multiline
                                        rowsMax={10}
                                        rows={1}
                                    />
                                )
                            }
                        </Grid>
                        <Divider className="TransparentDivider margin-bottom-24 margin-top-48" />

                        <div className="modal-footer padding-left-16">
                            <SaspButton
                                className="margin-right-8 text-blue background-white"
                                style={{
                                    color: props.color,
                                    backgroundColor: "#fff !important"
                                }}
                                onClick={handleClose}
                            >
                                Cancel
                            </SaspButton>

                            <SaspButton
                                className="margin-left-8 text-white"
                                onClick={handleSubmit({type: props.type})}
                            >
                                Email Now
                            </SaspButton>
                        </div>
                    </>
                )
                break;
            }
            case 'share': {
                str = (
                    <>
                        <Grid item xs={12}>
                            <NoSsr>
                                <div>
                                    <div {...getRootProps()}>
                                        <Label {...getInputLabelProps()}>Share With</Label>
                                        <InputWrapper ref={setAnchorEl} className={focused ? 'focused' : ''}>
                                            {value.map((option, index) => (
                                                <Tag label={option.title} {...getTagProps({ index })} />
                                            ))}
                                            <input {...getInputProps()} />
                                        </InputWrapper>
                                    </div>
                                    {groupedOptions.length > 0 
                                        ? (
                                            <Listbox {...getListboxProps()}>
                                                {groupedOptions.map((option, index) => (
                                                <li {...getOptionProps({ option, index })}>
                                                    <span>{option.title}</span>
                                                    <CheckIcon fontSize="small" />
                                                </li>
                                                ))}
                                            </Listbox>
                                        ) 
                                        : null
                                    }
                                </div>
                            </NoSsr>
                        </Grid>
                        <Divider className="TransparentDivider margin-bottom-24 margin-top-48" />

                        <div className="modal-footer padding-left-16">
                            <SaspButton
                                className="margin-right-8 text-blue background-white"
                                style={{
                                    color: props.color,
                                    backgroundColor: "#fff !important"
                                }}
                                onClick={handleClose}
                            >
                                Cancel
                            </SaspButton>

                            <SaspButton
                                className="margin-left-8 text-white"
                                onClick={handleSubmit({type: props.type})}
                            >
                                Share {textstr}
                            </SaspButton>
                        </div>
                    </>
                )
                break;
            }
            case 'reset': {
                str = (
                    <>
                        <Grid item xs={12}>
                            <p>Are you sure want to reset this {textstr}?</p>
                        </Grid>

                        <Divider className="TransparentDivider margin-bottom-24 margin-top-48" />

                        <div className="modal-footer padding-left-16">
                            <SaspButton
                                className="margin-right-8 text-blue background-white"
                                style={{
                                    color: props.color,
                                    backgroundColor: "#fff !important"
                                }}
                                onClick={handleClose}
                            >
                                Cancel
                            </SaspButton>

                            <SaspButton
                                className="margin-left-8 text-white"
                                onClick={handleSubmit({type: props.type})}
                            >
                                Reset Now
                            </SaspButton>
                        </div>
                    </>
                )
                break;
            }
            case 'complete': {
                str = (
                    <>
                        <Grid item xs={12}>
                            <p>Are you sure want to complete this {textstr}?</p>
                        </Grid>

                        <Divider className="TransparentDivider margin-bottom-24 margin-top-48" />

                        <div className="modal-footer padding-left-16">
                            <SaspButton
                                className="margin-right-8 text-blue background-white"
                                style={{
                                    color: props.color,
                                    backgroundColor: "#fff !important"
                                }}
                                onClick={handleClose}
                            >
                                Cancel
                            </SaspButton>

                            <SaspButton
                                className="margin-left-8 text-white"
                                onClick={handleSubmit({
                                    type: props.type, 
                                    qyear: props.yearvar, 
                                    gamemode: props.gameMode
                                })}
                            >
                                Complete Now
                            </SaspButton>
                        </div>
                    </>
                )
                break;
            }
            case 'removeNote': {
                str = (
                    <>
                        <Grid item xs={12}>
                            <p>Are you sure want to remove this note?</p>
                        </Grid>

                        <Divider className="TransparentDivider margin-bottom-24 margin-top-48" />

                        <div className="modal-footer padding-left-16">
                            <SaspButton
                                className="margin-right-8 text-blue background-white"
                                style={{
                                    color: props.color,
                                    backgroundColor: "#fff !important"
                                }}
                                onClick={handleClose}
                            >
                                Cancel
                            </SaspButton>

                            <SaspButton
                                className="margin-left-8 text-white"
                                onClick={handleSubmit({type: props.type})}
                            >
                                Remove
                            </SaspButton>
                        </div>
                    </>
                )
                break;
            }
            case 'note': {
                str = (
                    <>
                        <Grid item xs={12}>
                            {noteError 
                                ? (
                                    <TextField
                                        error
                                        className="width100"
                                        label="Note"
                                        value={note}
                                        onChange={handleChange('note')}
                                        multiline
                                        rowsMax={10}
                                        rows={3}
                                        helperText="Invalid Email Address"
                                    />
                                )
                                : (
                                    <TextField
                                        className="width100"
                                        label="Note"
                                        value={note}
                                        onChange={handleChange('note')}
                                        multiline
                                        rowsMax={10}
                                        rows={3}
                                    />
                                )
                            }
                        </Grid>
                        <Divider className="TransparentDivider margin-bottom-24 margin-top-48" />

                        <div className="modal-footer padding-left-16">
                            <SaspButton
                                className="margin-right-8 text-blue background-white"
                                style={{
                                    color: props.color,
                                    backgroundColor: "#fff !important"
                                }}
                                onClick={handleClose}
                            >
                                Cancel
                            </SaspButton>

                            <SaspButton
                                className="margin-left-8 text-white"
                                onClick={handleSubmit({type: props.type})}
                            >
                                Edit Now
                            </SaspButton>
                        </div>
                    </>
                )
                break;
            }
            default: {
                return null;
            }
        }

        if (typeof str == 'object') {
            return (
                <div className="width100">
                    {str}
                </div>
            )
        }  
    }

    const body = (
        <div 
            className={classes.paper}
        >
            <Typography className="DividerText">{props.title}</Typography>
            <Divider className="SASPDivider margin-bottom-16" />
            
            <div 
                id="simple-modal-description" 
                style={{fontSize: "1rem"}}
                className="width100"
            >
                {form('in div...')}
            </div>
        </div>
    );

    const button = () => {
        let btn;
        let btnClass="icon-widget";
        
        if (props.text) {
            switch(props.type) {
                case 'email': {
                    btn = (
                        <MailOutlineIcon className="text-blue" style={{ fontSize:"1.2rem" }}/>
                    )
                    break;
                }
                case 'complete': {
                    btn = (
                        <DoneIcon className="text-blue" style={{ fontSize:"1.2rem" }}/>
                    )
                    break;
                }
                case 'reset': {
                    btn = (
                        <CachedIcon className="text-blue" style={{ fontSize:"1.2rem" }}/>
                    )
                    break;
                }
                case 'share': {
                    btn = (
                        <ShareIcon className="text-blue" style={{ fontSize:"1.2rem" }} />
                    )
                    break;
                }
                case 'note': {
                    btn = (
                        <EditIcon className="text-blue" style={{ fontSize:"1.2rem" }} />
                        // <>{props.text}</>
                    )
                    break;
                }
                case 'removeNote': {
                    btnClass = "icon-widget background-orange";
                    btn = (
                        <EditIcon className="text-blue" style={{ fontSize:"1.2rem" }} />
                        // <>{props.text}</>
                    )
                    break;
                }
                default: {
                    btn = (
                        <InfoIcon className="text-blue" style={{ fontSize:"1.2rem" }}/>
                    )
                    break;
                }
            }

            return (
                <div 
                    className="text-blue flex-row-start width100"
                    onClick={handleOpen}
                >
                    <div className="margin-right-4">{btn}</div>
                    <div>{props.title}</div>
                </div>
            )
        } else {
            switch(props.type) {
                case 'email': {
                    btn = (
                        <MailOutlineIcon className="text-white" />
                    )
                    break;
                }
                case 'complete': {
                    btn = (
                        <DoneIcon className="text-white" />
                    )
                    break;
                }
                case 'reset': {
                    btn = (
                        <CachedIcon className="text-white" />
                    )
                    break;
                }
                case 'share': {
                    btn = (
                        <ShareIcon className="text-white" style={{ fontSize:"1.2rem" }} />
                    )
                    break;
                }
                case 'note': {
                    btn = (
                        <EditIcon className="text-white" style={{ fontSize:"1.2rem" }} />
                        // <>{props.text}</>
                    )
                    break;
                }
                case 'removeNote': {
                    btnClass = "icon-widget background-orange";
                    btn = (
                        <EditIcon className="text-white" style={{ fontSize:"1.2rem" }} />
                        // <>{props.text}</>
                    )
                    break;
                }
                default: {
                    btn = (
                        <InfoIcon className="text-white" />
                    )
                    break;
                }
            }

            return (
                <div className="flex-col-center">
                    <div>
                        <button 
                            type="button" 
                            className={btnClass} 
                            onClick={handleOpen} 
                            style={{
                                color: props.color, 
                                padding: "0",
                                // backgroundColor: "#5d5d5d" //disabled
                            }}
                        >
                            {btn}
                        </button>
                    </div>

                    {props.subtitle
                        ? (<div className="text-small">{props.subtitle}</div>)
                        : null
                    }
                </div>
            )
        }
        
    };

    return (
        <div className={cClasses}>
            {button()}
            <Modal
                open={open} // open={false}
                onClose={handleClose}
                aria-labelledby="simple-modal-title"
                aria-describedby="simple-modal-description"
                style={{
                    display:"flex",
                    flexDirection:"column",
                    justifyContent:"center",
                    alignItems:"center"
                }}
            >
                {body}
            </Modal>
        </div>
    )
}

export default ModalForm;